import Multiselect from "multiselect-react-dropdown"
import FieldInfoIcon from "../../components/field-info"
import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "../../components/ui/form"
import { Input } from "../../components/ui/input"
import { cn, parseAttributes } from "../../lib/utils"
import {
	DropdownConfig,
	type Attribute,
	type FieldProps,
} from "../../types/form-types"
import { Description } from "./components/description"
import { useFormStore } from "@/stores/use-form-store"
import { useLogicStore } from "@/stores/use-conditional-logic-store"
import { ErrorMessage } from "./components/error-message"

export const MultiSelectDropdown = ({
	form,
	field,
	commonAttributes,
}: FieldProps) => {
	const { label, infoIconText, infoIconType, description, infoIconRequired } =
		commonAttributes

	// Parse attributes to extract options
	const { options, allowOtherOption, textInputRequired, sortingOfOptions } =
		getMultiSelectInputAttributes(field.attributes)
	const { shouldDisable, verifiedFields } = useFormStore()

	const isVerified = verifiedFields.get(field.field_id) || false
	const { hiddenFields, errors } = useLogicStore()

	if (hiddenFields[field.field_id]) return null

	let sortedOptions: { label: string; value: string }[] = options || []
	if (sortingOfOptions) {
		if (sortingOfOptions === "ALPHABETICAL") {
			sortedOptions = [...sortedOptions].sort((a, b) =>
				a.label.localeCompare(b.label),
			)
		} else if (sortingOfOptions === "ALPHABETICAL_DESCENDING") {
			sortedOptions = [...sortedOptions].sort((a, b) =>
				b.label.localeCompare(a.label),
			)
		}
	}

	return (
		<FormField
			control={form.control}
			name={field.field_id}
			render={({ field: formField }) => (
				<FormItem>
					<FormLabel>
						<div className="flex flex-col gap-1">
							<div className="flex items-center gap-1">
								<p
									className={cn(
										field.is_required &&
											"after:ml-1 after:text-red-500 after:content-['*']",
									)}
								>
									{label}
								</p>
								{infoIconRequired && (
									<FieldInfoIcon
										infoText={infoIconText}
										iconType={infoIconType}
									/>
								)}
							</div>
							{description && (
								<Description description={description} />
							)}
						</div>
					</FormLabel>
					<FormControl>
						<div className="relative h-fit">
							<Multiselect
								disable={isVerified || shouldDisable}
								style={{
									optionContainer: {
										backgroundColor:
											"hsl(var(--background))",
									},
									optionListContainer: {
										backgroundColor:
											"hsl(var(--background))",
									},
								}}
								displayValue="name"
								options={sortedOptions.map(opt => ({
									name: opt.label,
									id: opt.value,
								}))}
								// RHF expects array of strings
								selectedValues={(
									formField?.value?.split?.(",") || []
								)
									.map((v: string) => {
										const found = sortedOptions.find(
											opt => opt.value === v,
										)
										return found
											? {
													name: found.label,
													id: found.value,
												}
											: null
									})
									.filter(Boolean)}
								onSelect={selectedList => {
									const valuesOnly = selectedList.map(
										(item: { name: string; id: string }) =>
											item.id,
									) // 👈 extract just values
									formField.onChange(valuesOnly?.join(","))
								}}
								onRemove={selectedList => {
									const valuesOnly = selectedList.map(
										(item: { name: string; id: string }) =>
											item.id,
									)
									formField.onChange(valuesOnly?.join(","))
								}}
								placeholder="Select options"
								showCheckbox
								avoidHighlightFirstOption
							/>

							{/* Allow "Other" input if configured */}
							{allowOtherOption && textInputRequired && (
								<Input
									type="text"
									className="df-input mt-2 w-full"
									placeholder="Enter other value"
									onBlur={e => {
										if (e.target.value) {
											const updated = [
												...(formField.value || []),
												e.target.value,
											]
											formField.onChange(updated)
										}
									}}
								/>
							)}
						</div>
					</FormControl>
					<FormMessage />
					<ErrorMessage error={errors[field.field_id]} />
				</FormItem>
			)}
		/>
	)
}

function getMultiSelectInputAttributes(attributes: Attribute[]) {
	const props: {
		options: { label: string; value: string }[]
		allowOtherOption: boolean
		textInputRequired: boolean
		defaultSelection: string[]
		sortingOfOptions?: string
	} = {
		options: [],
		allowOtherOption: false,
		textInputRequired: false,
		defaultSelection: [],
	}
	const parsed = parseAttributes(attributes)

	Object.entries(parsed).forEach(([key, value]) => {
		switch (key) {
			case DropdownConfig.OptionList:
				props.options = value as { label: string; value: string }[]
				break
			case DropdownConfig.AllowOtherOption:
				props.allowOtherOption = value as boolean
				break
			case DropdownConfig.TextInputRequired:
				props.textInputRequired = value as boolean
				break
			case DropdownConfig.SortingOfOptions:
				props.sortingOfOptions = value as string
				break
		}
	})

	return props
}
